import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import { createPortal } from 'react-dom';
import { getOperatorsForTransactionType } from '../config/transactionFilterProperties';
import { getDataTypeIcon } from './SessionPropertyFilterModule';

const OperatorDropdown = ({ value, operators, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const safeOperators = Array.isArray(operators) && operators.length > 0 ? operators : [{ value: 'is', label: 'Is' }];
  const selectedOperator = safeOperators.find((op) => op.value === value) || safeOperators[0];

  React.useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 cursor-pointer hover:bg-gray-200 transition-colors min-w-[100px]"
      >
        {selectedOperator.label}
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[99998]" onClick={() => setIsOpen(false)} />
            <div
              ref={dropdownRef}
              className="fixed z-[99999] bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${position.width || 100}px`,
                minWidth: '100px',
              }}
            >
              <div className="py-1">
                {safeOperators.map((op) => (
                  <button
                    key={op.value}
                    type="button"
                    onClick={() => { onChange(op.value); setIsOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${value === op.value ? 'bg-gray-200 text-[#0f172a]' : ''}`}
                  >
                    <span>{op.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

const MultiSelectDropdown = ({ value = [], options = [], onChange, placeholder = 'Select...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 120 });
  React.useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 180) });
    }
  }, [isOpen]);
  const selectedArray = Array.isArray(value) ? value : (value != null ? [value] : []);
  const toggle = (opt) => {
    const next = selectedArray.includes(opt) ? selectedArray.filter((o) => o !== opt) : [...selectedArray, opt];
    onChange(next);
  };
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 cursor-pointer hover:bg-gray-200 transition-colors min-w-[120px] text-left flex items-center justify-between gap-1"
      >
        <span className={selectedArray.length === 0 ? 'text-[#475569]' : ''}>
          {selectedArray.length === 0 ? placeholder : `${selectedArray.length} selected`}
        </span>
        <ChevronDown className={`h-4 w-4 text-[#475569] flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[99998]" onClick={() => setIsOpen(false)} />
            <div
              ref={dropdownRef}
              className="fixed z-[99999] bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto min-w-[180px]"
              style={{ top: `${pos.top}px`, left: `${pos.left}px`, width: `${Math.max(pos.width, 180)}px` }}
            >
              {(options || []).length === 0 ? (
                <div className="px-3 py-2 text-xs text-[#475569]">No options loaded</div>
              ) : (options || []).map((opt) => {
                const val = typeof opt === 'object' ? opt.value ?? opt.label : opt;
                const label = typeof opt === 'object' ? opt.label ?? opt.value : opt;
                const isSelected = selectedArray.includes(val);
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => toggle(val)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                      isSelected
                        ? 'bg-[#4b57aa] text-white hover:bg-[#4b57aa]'
                        : 'text-[#0f172a] hover:bg-gray-50'
                    }`}
                  >
                    <input type="checkbox" checked={isSelected} readOnly className="rounded" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </>
  );
};

/**
 * Chip for session-scope filters. Uses same operators and value inputs as Events/Transactions
 * (getOperatorsForTransactionType: string, number, boolean, datetime).
 */
const SessionFilterChip = ({
  filter,
  isFirst,
  onEdit,
  onRemove,
  onUpdate,
  fieldOptions = {},
}) => {
  const connector = filter.connector || (isFirst ? 'WHERE' : 'AND');
  const type = filter.propertyType || 'string';
  const DataTypeIcon = getDataTypeIcon(type);
  const operators = getOperatorsForTransactionType(type);
  const currentOperator = filter.operator || operators[0]?.value;
  const options = fieldOptions[filter.field] || [];

  const renderConnector = () => {
    if (isFirst) {
      return <span className="text-xs font-medium text-[#475569] px-2">WHERE</span>;
    }
    return (
      <select
        value={connector}
        onChange={(e) => onUpdate({ connector: e.target.value })}
        className="px-2 py-1 text-xs bg-gray-100 rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
        onClick={(e) => e.stopPropagation()}
      >
        <option value="AND">AND</option>
        <option value="OR">OR</option>
      </select>
    );
  };

  const renderValueInput = () => {
    if (type === 'string') {
      if (currentOperator === 'is_set' || currentOperator === 'is_not_set') return null;
      if (currentOperator === 'is' || currentOperator === 'is_not') {
        if (!options || options.length === 0) {
          return (
            <input
              type="text"
              value={Array.isArray(filter.value) ? filter.value[0] ?? '' : (filter.value ?? '')}
              onChange={(e) => onUpdate({ value: e.target.value.trim() || null })}
              placeholder="Type value..."
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-28"
              onClick={(e) => e.stopPropagation()}
            />
          );
        }
        return (
          <MultiSelectDropdown
            value={Array.isArray(filter.value) ? filter.value : filter.value != null ? [filter.value] : []}
            options={options}
            onChange={(v) => onUpdate({ value: v })}
            placeholder="Select values..."
          />
        );
      }
      return (
        <input
          type="text"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value || null })}
          placeholder="Enter value..."
          className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-28"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }
    if (type === 'number') {
      if (currentOperator === 'is_numeric' || currentOperator === 'is_not_numeric') return null;
      if (currentOperator === 'between' || currentOperator === 'not_between') {
        return (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={filter.value ?? ''}
              onChange={(e) => onUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
              placeholder="From"
              className="px-2 py-1 text-xs bg-gray-100 rounded w-16"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="text-xs text-[#475569]">to</span>
            <input
              type="number"
              value={filter.value2 ?? ''}
              onChange={(e) => onUpdate({ value2: e.target.value === '' ? null : parseFloat(e.target.value) })}
              placeholder="To"
              className="px-2 py-1 text-xs bg-gray-100 rounded w-16"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      }
      return (
        <input
          type="number"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
          placeholder="Value"
          className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg w-24"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }
    if (type === 'boolean') {
      return null;
    }
    if (type === 'datetime' || type === 'date') {
      if (currentOperator === 'last' || currentOperator === 'not_in_the_last') {
        return (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={filter.value ?? ''}
              onChange={(e) => onUpdate({ value: e.target.value === '' ? null : e.target.value })}
              placeholder="X"
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded w-16"
              onClick={(e) => e.stopPropagation()}
            />
            <select
              value={filter.value2 || 'days'}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              className="px-2 py-1.5 text-xs bg-gray-100 rounded"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
              <option value="years">years</option>
            </select>
            {currentOperator === 'not_in_the_last' && <span className="text-xs text-[#475569]">ago</span>}
          </div>
        );
      }
      if (currentOperator === 'between' || currentOperator === 'not_between') {
        return (
          <div className="flex items-center gap-1">
            <input
              type="datetime-local"
              value={filter.value ? new Date(filter.value).toISOString().slice(0, 16) : ''}
              onChange={(e) => onUpdate({ value: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="px-2 py-1 text-xs bg-gray-100 rounded"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="text-xs text-[#475569]">to</span>
            <input
              type="datetime-local"
              value={filter.value2 ? new Date(filter.value2).toISOString().slice(0, 16) : ''}
              onChange={(e) => onUpdate({ value2: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="px-2 py-1 text-xs bg-gray-100 rounded"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      }
      if (['on', 'not_on', 'before', 'since', 'in_the_next', 'before_the_last'].includes(currentOperator)) {
        if (currentOperator === 'before_the_last' || currentOperator === 'in_the_next') {
          return (
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={filter.value ?? ''}
                onChange={(e) => onUpdate({ value: e.target.value === '' ? null : e.target.value })}
                placeholder="X"
                className="px-2.5 py-1.5 text-xs bg-gray-100 rounded w-16"
                onClick={(e) => e.stopPropagation()}
              />
              <select
                value={filter.value2 || 'days'}
                onChange={(e) => onUpdate({ value2: e.target.value })}
                className="px-2 py-1.5 text-xs bg-gray-100 rounded"
                onClick={(e) => e.stopPropagation()}
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
                <option value="weeks">weeks</option>
                <option value="months">months</option>
                <option value="years">years</option>
              </select>
              {(currentOperator === 'before_the_last' || currentOperator === 'in_the_next') && currentOperator === 'before_the_last' && <span className="text-xs text-[#475569]">ago</span>}
            </div>
          );
        }
        return (
          <input
            type="datetime-local"
            value={filter.value ? new Date(filter.value).toISOString().slice(0, 16) : ''}
            onChange={(e) => onUpdate({ value: e.target.value ? new Date(e.target.value).toISOString() : null })}
            className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        );
      }
    }
    return (
      <input
        type="text"
        value={Array.isArray(filter.value) ? filter.value.join(', ') : (filter.value ?? '')}
        onChange={(e) => onUpdate({ value: e.target.value.trim() || null })}
        placeholder="Value"
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg w-28"
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  const handleOperatorChange = (newOp) => {
    const updates = { operator: newOp, value2: null };
    if (type === 'boolean') {
      updates.value = newOp === 'is_true';
    } else if (!['is_set', 'is_not_set', 'is_true', 'is_false', 'is_numeric', 'is_not_numeric'].includes(newOp)) {
      updates.value = filter.value;
    } else {
      updates.value = null;
    }
    onUpdate(updates);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {renderConnector()}
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(); } }}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
      >
        <DataTypeIcon className="h-4 w-4 text-[#475569] flex-shrink-0" />
        <span className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569]">S</span>
        <span className="text-sm text-[#0f172a] font-medium">{filter.propertyLabel}</span>
      </div>
      <OperatorDropdown
        value={currentOperator}
        operators={operators}
        onChange={handleOperatorChange}
      />
      {renderValueInput()}
      <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="p-1.5 text-[#475569] hover:text-[#475569]" title="Delete filter">
        <Icon name="delete" size={16} />
      </button>
    </div>
  );
};

export default SessionFilterChip;
